import { useRef, useState, useEffect, useCallback } from 'react';
import styles from './ScrollableX.module.css';

interface UseScrollableProps {
  minDistanceForSnap?: number;
  scrollDistance?: number;
}

const defaultProps: Required<UseScrollableProps> = {
  minDistanceForSnap: 250,
  scrollDistance: 250,
};

const DRAG_DISTANCE_THRESHOLD = 10;
const SCROLL_TOLERANCE = 2;

export const useScrollable = ({ minDistanceForSnap, scrollDistance }: UseScrollableProps = {}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  /**
   * Update scroll position of the container
   * @returns
   */
  const updateScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const atStart = container.scrollLeft === 0;
    const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - SCROLL_TOLERANCE;

    if (isAtStart !== atStart) setIsAtStart(atStart);
    if (isAtEnd !== atEnd) setIsAtEnd(atEnd);
  }, [isAtStart, isAtEnd]);

  /**
   * Scroll the container a given distance in left direction
   * @param distance pass a number to scroll given distance into direction
   * @returns
   */
  const scrollToLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft } = container;
    const snapThreshold = minDistanceForSnap ?? defaultProps.minDistanceForSnap;

    let targetScrollLeft = scrollLeft - (scrollDistance ?? defaultProps.scrollDistance);

    if (targetScrollLeft <= snapThreshold) {
      targetScrollLeft = 0;
    } else {
      targetScrollLeft = Math.max(targetScrollLeft, 0);
    }

    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }, [minDistanceForSnap, scrollDistance]);

  /**
   * Scroll the container a given distance in right direction
   * @param distance pass a number to scroll given distance into direction
   * @returns
   */
  const scrollToRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const snapThreshold = minDistanceForSnap ?? defaultProps.minDistanceForSnap;

    let targetScrollLeft = scrollLeft + (scrollDistance ?? defaultProps.scrollDistance);

    const maxScrollLeft = scrollWidth - clientWidth;
    if (maxScrollLeft - targetScrollLeft <= snapThreshold) {
      targetScrollLeft = maxScrollLeft;
    } else {
      targetScrollLeft = Math.min(targetScrollLeft, maxScrollLeft);
    }

    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }, [minDistanceForSnap, scrollDistance]);

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let isDragging = false;
    let startX = 0;
    let initialScrollLeft = container.scrollLeft;
    let lastX = 0;
    let lastTime = performance.now();
    let velocity = 0;

    const isTouchEvent = e.type === 'touchstart';

    // Determine starting position
    if (isTouchEvent) {
      const touchEvent = e as React.TouchEvent;
      startX = touchEvent.touches[0].clientX;
      lastX = startX;
    } else {
      const mouseEvent = e as React.MouseEvent;
      startX = mouseEvent.clientX;
      lastX = startX;
    }

    const onMove = (moveEvent: MouseEvent | TouchEvent) => {
      let currentX = 0;

      if (moveEvent.type === 'touchmove') {
        const touchMoveEvent = moveEvent as TouchEvent;
        currentX = touchMoveEvent.touches[0].clientX;
      } else {
        const mouseMoveEvent = moveEvent as MouseEvent;
        currentX = mouseMoveEvent.clientX;
      }

      const dx = lastX - currentX;

      if (Math.abs(dx) >= DRAG_DISTANCE_THRESHOLD) {
        isDragging = true;
        velocity = dx / (performance.now() - lastTime);
        lastTime = performance.now();
        container.classList.add(styles.noPointerEvents);
      }

      if (isDragging) {
        container.scrollLeft = initialScrollLeft + (startX - currentX);
        updateScrollPosition();
      }
    };

    const onEnd = () => {
      container.classList.add(styles.smoothScrolling);
      container.classList.remove(styles.noPointerEvents);

      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);

      container.scrollLeft = container.scrollLeft + velocity;

      isDragging = false;
    };

    container.classList.remove(styles.smoothScrolling);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onEnd);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollPosition);
      updateScrollPosition();

      // Disable default drag behavior for all child elements
      const children = container.querySelectorAll<HTMLElement>(':scope > *');
      children.forEach(child => {
        child.setAttribute('draggable', 'false');
        child.addEventListener('dragstart', e => e.preventDefault());
      });
    }

    return () => {
      container?.removeEventListener('scroll', updateScrollPosition);

      // Cleanup dragstart listeners
      const children = container?.querySelectorAll<HTMLElement>(':scope > *');
      children?.forEach(child => {
        child.removeEventListener('dragstart', e => e.preventDefault());
      });
    };
  }, [updateScrollPosition]);

  return { scrollContainerRef, isAtStart, isAtEnd, scrollToLeft, scrollToRight, handleDrag };
};
